import { useCallback, useEffect } from 'react';
import {
  fabricObjectProperties,
  indicatorTransparentColor,
  indicatorColor,
  getCurrentZoom,
  strokeWidthThin,
  shouldShowFinding,
  centerToObject,
  getStrokeColor
} from '../Helpers';
import { ImageOverlap } from '../../Annotation/annotation.types';
import { Overlap } from '../canvas.slice';
import { fabric } from 'fabric';
import { AnnotatedFinding } from '../prepareShapesSelector';
import { FabricObjectWithSrc } from '../../../../shared/Helpers';
import { makePolygonEditable } from './Helpers';

const overlappPolygonName = 'overlapp-area';

const useFabricObjects = (
  canvasEl: fabric.Canvas | null,
  overlappingDto: any,
  scale: number,
  toggleOverlappingVisibility: boolean,
  shapes: AnnotatedFinding[] | null,
  toggleAllUserFindingsVisibility: boolean,
  toggleMLFindingsVisibility: boolean,
  height: number,
  image: Nullable<FabricObjectWithSrc>,
  loaded: boolean,
  createAnnotationCanceled: boolean,
  selectedAnnotationId: string,
  shouldZoomToFinding: boolean,
  havePermissionToUse: boolean,
  contrast: number,
  brightness: number,
  saturation: number,
  viewportTransform: number[] | undefined,
  onShouldZoomToFinding: (shouldZoom: boolean) => void,
  onScaledOverlapping: (overlapping: Overlap[]) => void,
  onImageLoaded: () => void,
  resetCreateAnnotationCanceled: () => void,
  preloadClosest: () => void
) => {
  const renderOverlapping = useCallback(() => {
    if (overlappingDto && canvasEl) {
      canvasEl.remove(...canvasEl.getObjects('polygon').filter(x => x.name === overlappPolygonName));

      if (!toggleOverlappingVisibility) {
        return;
      }

      let scaledOverlappingItems: Overlap[] = [];
      overlappingDto.forEach((overlap: ImageOverlap) => {
        let scaledShape = overlap.shape!.map(point => ({
          x: point.x * scale,
          y: point.y * scale
        }));

        const scaledOverlap: Overlap = {
          shape: scaledShape,
          isActive: overlap.isVisible
        };

        scaledOverlappingItems.push(scaledOverlap);

        let overlapPolygonSettings = { ...fabricObjectProperties };
        if (overlap.isVisible) {
          overlapPolygonSettings.fill = indicatorTransparentColor;
        }

        overlapPolygonSettings.evented = false;
        overlapPolygonSettings.stroke = indicatorColor;
        overlapPolygonSettings.selectable = false;
        overlapPolygonSettings.lockMovementX = true;
        overlapPolygonSettings.lockMovementY = true;
        overlapPolygonSettings.lockScalingX = true;
        overlapPolygonSettings.lockScalingY = true;
        overlapPolygonSettings.lockRotation = true;
        overlapPolygonSettings.lockUniScaling = true;

        const zoom = getCurrentZoom(canvasEl);
        const polygon = new fabric.Polygon(scaledShape, {
          ...overlapPolygonSettings,
          strokeWidth: strokeWidthThin / zoom,
          name: overlappPolygonName
        });
        canvasEl.add(polygon);
        canvasEl.renderAll();
        const allObjects = canvasEl.getObjects();
        allObjects.forEach(x => canvasEl.bringToFront(x));
      });
      onScaledOverlapping(scaledOverlappingItems);
    }
  }, [overlappingDto, canvasEl, toggleOverlappingVisibility, onScaledOverlapping, scale]);

  const loadFindings = useCallback(() => {
    if (shapes && canvasEl) {
      canvasEl.remove(...canvasEl.getObjects('rect'));
      canvasEl.remove(...canvasEl.getObjects('polygon'));

      shapes.forEach(shape => {
        if (!shouldShowFinding(toggleAllUserFindingsVisibility, toggleMLFindingsVisibility, shape.isReviewed || shape.isQaReviewed)) {
          return;
        }

        let newShape = shape.shape.map(x => ({
          x: x.x * scale,
          y: x.y * scale
        }));

        const zoom = getCurrentZoom(canvasEl);

        const FINDING_FAKE_COLOR = 'rgba(255, 255, 255, 0.01)';
        const polygon = new fabric.Polygon(newShape, {
          name: shape.id,
          ...fabricObjectProperties,
          stroke: getStrokeColor(shape),
          strokeWidth: strokeWidthThin / zoom,
          evented: havePermissionToUse,
          lockMovementX: shape.disabled,
          lockMovementY: shape.disabled,
          lockScalingX: shape.disabled,
          lockScalingY: shape.disabled,
          fill: FINDING_FAKE_COLOR
        });

        makePolygonEditable(polygon, canvasEl);

        if (shape.disabled) {
          (polygon as any).edit = false;
          (polygon as any).controls = undefined;
        }

        canvasEl.add(polygon);
        canvasEl.renderAll();

        const allObjects = canvasEl.getObjects();
        allObjects.forEach(x => canvasEl.bringToFront(x));
      });
    }
  }, [canvasEl, havePermissionToUse, scale, shapes, toggleAllUserFindingsVisibility, toggleMLFindingsVisibility]);

  useEffect(() => {
    if (!image || !height || !canvasEl || !loaded) return;

    const bgImage = canvasEl.getObjects('image')[0];

    const contrastFilter = new fabric.Image.filters.Contrast({
      contrast: contrast
    });
    const brightnessFilter = new fabric.Image.filters.Brightness({
      brightness: brightness
    });
    const saturationFilter = new fabric.Image.filters.Saturation({
      saturation: saturation
    });

    if (bgImage) {
      (bgImage as fabric.Image).applyFilters([contrastFilter, brightnessFilter, saturationFilter]);
    }
    canvasEl.requestRenderAll();
  }, [brightness, canvasEl, contrast, height, image, loaded, saturation]);

  useEffect(() => {
    if (!image) {
      if (canvasEl) {
        canvasEl.remove(...canvasEl.getObjects());
        canvasEl.renderAll();
        if (viewportTransform) {
          canvasEl.setViewportTransform(viewportTransform);
        } else {
          canvasEl.setViewportTransform([1, 0, 0, 1, 0, 0]);
        }
      }
    }

    if (!image || !height) return;

    if (loaded) {
      loadFindings();
      renderOverlapping();
      return;
    }

    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = image.src;
    img.onload = event => {
      const fabricImage = new fabric.Image(img, image);
      fabricImage.evented = false;

      if (canvasEl) {
        canvasEl.remove(...canvasEl.getObjects());
        canvasEl.add(fabricImage);
        canvasEl.renderAll();

        loadFindings();
        renderOverlapping();

        if (viewportTransform) {
          canvasEl.setViewportTransform(viewportTransform);
        } else {
          canvasEl.setViewportTransform([1, 0, 0, 1, 0, 0]);
        }
      }
      onImageLoaded();
      preloadClosest();
    };
  }, [image, height, canvasEl, onImageLoaded, loadFindings, renderOverlapping, loaded, preloadClosest]);

  useEffect(() => {
    if (createAnnotationCanceled && canvasEl && canvasEl._objects.length) {
      const shapesArray = canvasEl._objects;
      const canceledShape = shapesArray[shapesArray.length - 1];
      canvasEl.remove(canceledShape);
      resetCreateAnnotationCanceled();

      loadFindings();
      renderOverlapping();
    }
  }, [createAnnotationCanceled, resetCreateAnnotationCanceled, canvasEl, loadFindings, renderOverlapping]);

  useEffect(() => {
    if (canvasEl && loaded) {
      const allObjects = canvasEl.getObjects();
      const selectedObject = allObjects.find(x => x.name === selectedAnnotationId);
      if (selectedObject) {
        if (selectedObject !== canvasEl.getActiveObject()) {
          canvasEl.setActiveObject(selectedObject);

          if (shouldZoomToFinding) {
            setTimeout(() => {
              centerToObject(canvasEl, selectedObject);
            }, 200);
            onShouldZoomToFinding(false);
          }
        }
      } else {
        canvasEl.discardActiveObject();
      }
      canvasEl.renderAll();
    }
  }, [canvasEl, selectedAnnotationId, loaded, shouldZoomToFinding, onShouldZoomToFinding]);
};

export default useFabricObjects;
